/**
 * Variant Metafields Dynamic Update
 * Updates Dimensions and Product Details sections when variant changes
 * Reads data from #variant-metafields-data JSON script block
 */
(function() {
  'use strict';

  let variantData = null;
  let defaultMessage = 'Please contact our team for full product specifications. PHONE 1-800-876-3194';

  /**
   * Initialize variant metafields data from JSON script block
   */
  function initializeData() {
    const dataScript = document.getElementById('variant-metafields-data');
    if (!dataScript) return false;

    try {
      const data = JSON.parse(dataScript.textContent);
      variantData = data.variants || {};
      defaultMessage = data.defaultMessage || defaultMessage;
      return true;
    } catch (e) {
      console.error('Failed to parse variant metafields data:', e);
      return false;
    }
  }

  /**
   * Escape HTML to prevent XSS
   * @param {string} text - Text to escape
   * @returns {string} Escaped text
   */
  function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Update Dimensions section with variant data
   * Hides the parent .shopify-block completely if no dimensions data exists (avoids gap issues)
   * @param {string|null} content - Dimensions content (newline-separated)
   */
  function updateDimensions(content) {
    const dimensionsEl = document.getElementById('variant-dimensions');
    const dimensionsWrapper = document.getElementById('dimensions-accordion-wrapper');

    if (!dimensionsEl) return;

    // Find the parent .shopify-block to hide (this is what causes the gap)
    const shopifyBlock = dimensionsWrapper ? dimensionsWrapper.closest('.shopify-block') : null;

    if (content && content.trim() !== '') {
      // Show block and update content
      if (shopifyBlock) {
        shopifyBlock.style.display = '';
      }
      if (dimensionsWrapper) {
        dimensionsWrapper.style.display = '';
      }
      const lines = content.split('\n').filter(line => line.trim());
      dimensionsEl.innerHTML = lines
        .map(line => '<p class="dimension-line">' + escapeHtml(line.trim()) + '</p>')
        .join('');
    } else {
      // Hide the entire shopify-block (not just wrapper) to avoid gap
      if (shopifyBlock) {
        shopifyBlock.style.display = 'none';
      }
      dimensionsEl.innerHTML = '';
    }
  }

  /**
   * Update Product Details section with variant data
   * @param {string|null} content - Details content (newline-separated)
   */
  function updateDetails(content) {
    const detailsEl = document.getElementById('variant-details');
    if (!detailsEl) return;

    if (content && content.trim() !== '') {
      const lines = content.split('\n').filter(line => line.trim());
      detailsEl.innerHTML = '<ul class="details-list">' +
        lines.map(line => '<li>' + escapeHtml(line.trim()) + '</li>').join('') +
        '</ul>';
    } else {
      detailsEl.innerHTML = '<p class="default-message">' + escapeHtml(defaultMessage) + '</p>';
    }
  }

  /**
   * Update both sections for a given variant ID
   * @param {string} variantId - The variant ID to load data for
   */
  function updateVariantMetafields(variantId) {
    if (!variantData) {
      if (!initializeData()) return;
    }

    const data = variantData[variantId];
    if (!data) {
      // Variant not found, show default messages
      updateDimensions(null);
      updateDetails(null);
      return;
    }

    updateDimensions(data.dimensions);
    updateDetails(data.details);
  }

  /**
   * Extract variant ID from various event formats
   * @param {Event} event - The variant change event
   * @returns {string|null} The variant ID or null
   */
  function getVariantIdFromEvent(event) {
    // Try event.detail.resource.id (VariantUpdateEvent format)
    if (event.detail?.resource?.id) {
      return String(event.detail.resource.id);
    }
    // Try event.detail.variant.id
    if (event.detail?.variant?.id) {
      return String(event.detail.variant.id);
    }
    // Try event.detail.id
    if (event.detail?.id) {
      return String(event.detail.id);
    }
    return null;
  }

  /**
   * Initialize event listeners
   */
  function init() {
    // Initialize data on load
    initializeData();

    // Listen for theme's variant:update event (from VariantUpdateEvent)
    document.addEventListener('variant:update', function(event) {
      const variantId = getVariantIdFromEvent(event);
      if (variantId) {
        updateVariantMetafields(variantId);
      }
    });

    // Listen for variant:selected event
    document.addEventListener('variant:selected', function(event) {
      // This event fires before the full update, we can use it too
      if (event.detail?.resource?.id) {
        updateVariantMetafields(String(event.detail.resource.id));
      }
    });

    // Fallback: Listen for changes on variant select/input elements
    document.addEventListener('change', function(event) {
      const target = event.target;
      if (target.matches('select[name="id"], input[name="id"]')) {
        updateVariantMetafields(target.value);
      }
    });

    // Fallback: MutationObserver for hidden variant input changes
    const variantInput = document.querySelector('input[name="id"][type="hidden"], select[name="id"]');
    if (variantInput) {
      const observer = new MutationObserver(function(mutations) {
        mutations.forEach(function(mutation) {
          if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
            updateVariantMetafields(variantInput.value);
          }
        });
      });
      observer.observe(variantInput, { attributes: true, attributeFilter: ['value'] });
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
